/**
 * Unit conversion + display formatting for the customer SPA.
 *
 * Weight is stored as value + unit (docs/v4/04 §1) so it never lossy-converts
 * for display. Conversion here is for *compute* (fuel calc) and for showing a
 * value in the user's preferred unit — never to re-persist.
 */

// ─── Weight ──────────────────────────────────────────────────────────────

export type WeightUnit = "kg" | "lb";

const LB_PER_KG = 2.2046226218;

export function kgToLb(kg: number): number {
  return kg * LB_PER_KG;
}

export function lbToKg(lb: number): number {
  return lb / LB_PER_KG;
}

/** Normalise any stored {value, unit} to kilograms for the fuel calculation. */
export function weightToKg(value: number, unit: WeightUnit): number {
  return unit === "kg" ? value : lbToKg(value);
}

/** Display a stored weight in its own unit (no round-trip conversion). */
export function formatWeight(value: number | null | undefined, unit: WeightUnit | null | undefined): string {
  if (value == null || unit == null) return "—";
  return `${roundTo(value, 1)} ${unit}`;
}

// ─── Distance ────────────────────────────────────────────────────────────

export type DistanceUnit = "km" | "mi";

const MI_PER_KM = 0.6213711922;

export function kmToMi(km: number): number {
  return km * MI_PER_KM;
}

export function miToKm(mi: number): number {
  return mi / MI_PER_KM;
}

/** Convert a stored km value into the display unit's numeric value. */
function toDisplayDistance(km: number, unit: DistanceUnit): number {
  return unit === "km" ? km : kmToMi(km);
}

/**
 * `PlannedWorkout.distanceKm` is stored in km — format it in the chosen unit.
 * Trailing `.0` is trimmed (`10 km`, `6.2 mi`); empty string for a missing value.
 * Used where a terse, dash-free figure is wanted (e.g. Dashboard rows).
 */
export function formatDistance(km: number | null | undefined, unit: DistanceUnit = "km"): string {
  if (km == null) return "";
  return `${roundTo(toDisplayDistance(km, unit), 1)} ${unit}`;
}

/**
 * Stat/table variant: always one decimal (`32.0 km`, `19.9 mi`), em dash for a
 * missing or non-positive distance. Mirrors the old `Activities/format` copy.
 */
export function formatDistanceStat(km: number | null | undefined, unit: DistanceUnit = "km"): string {
  if (km == null || km <= 0) return "—";
  return `${toDisplayDistance(km, unit).toFixed(1)} ${unit}`;
}

/**
 * Calendar/list chip variant: always one decimal, assumes a real number (the
 * caller guards for null). Mirrors the old `dates.formatDist` copy.
 */
export function formatDistanceChip(km: number, unit: DistanceUnit = "km"): string {
  return `${toDisplayDistance(km, unit).toFixed(1)} ${unit}`;
}

/**
 * Average pace from distance + duration, in the chosen unit (`5:30 /km`,
 * `8:51 /mi`). Em dash when distance or duration is non-positive.
 */
export function formatPace(
  km: number | null | undefined,
  durationMin: number,
  unit: DistanceUnit = "km"
): string {
  if (km == null || km <= 0 || durationMin <= 0) return "—";
  const minPerUnit = durationMin / toDisplayDistance(km, unit);
  const m = Math.floor(minPerUnit);
  const s = Math.round((minPerUnit - m) * 60);
  // Carry a 60s rounding overflow into the minutes place.
  const mm = s === 60 ? m + 1 : m;
  const ss = s === 60 ? 0 : s;
  return `${mm}:${String(ss).padStart(2, "0")} /${unit === "km" ? "km" : "mi"}`;
}

// ─── Elevation ───────────────────────────────────────────────────────────

const FT_PER_M = 3.280839895;

export function mToFt(m: number): number {
  return m * FT_PER_M;
}

/**
 * Elevation gain, stored in metres. Shown in feet when the distance unit is
 * imperial (`mi`), otherwise metres. Em dash for a missing/non-positive value.
 */
export function formatElevation(m: number | null | undefined, unit: DistanceUnit = "km"): string {
  if (m == null || m <= 0) return "—";
  return unit === "mi" ? `${Math.round(mToFt(m))} ft` : `${Math.round(m)} m`;
}

// ─── Macros & price ──────────────────────────────────────────────────────

/** Carbs / protein etc. — whole grams. */
export function formatGrams(grams: number | null | undefined): string {
  if (grams == null) return "—";
  return `${Math.round(grams)}g`;
}

/** Sodium — milligrams. */
export function formatMg(mg: number | null | undefined): string {
  if (mg == null) return "—";
  return `${Math.round(mg)}mg`;
}

/** Caffeine — milligrams, but `0` reads as "none". */
export function formatCaffeine(mg: number | null | undefined): string {
  if (mg == null) return "—";
  return mg === 0 ? "none" : `${Math.round(mg)}mg`;
}

/** Price — integer cents → "$12.40". */
export function formatPrice(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

// ─── internal ────────────────────────────────────────────────────────────

function roundTo(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}
